import React, {useState, useRef, useEffect, useCallback} from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, LinearProgress, Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

export interface FileUploadActionState {
    canUpload: boolean;
    uploading: boolean;
}

export interface FileUploadDialogActionRef {
    cancel: () => void;
    upload: () => void;
    getState: () => FileUploadActionState;
}

export interface FileUploadDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called with the upload result when the upload succeeds; the dialog auto-closes */
    onSuccess: (result: any) => void;
    /** Called when the uploading state changes */
    onUploadingChange?: (v: boolean) => void;
    /**
     * Upload API. When {@code multiple} is false (default), receives a single File.
     * When {@code multiple} is true, receives File[].
     */
    onUpload: ((file: File) => Promise<any>) | ((files: File[]) => Promise<any>);
    /** Dialog title (default: "Import Excel") */
    title?: string;
    /** Accepted file extensions, e.g. ".xlsm" or ".xlsx,.xlsm" (default: ".xlsm") */
    accept?: string;
    /** Label shown below the drop zone (default: "Accepted: .xlsm only · Max size: 10MB") */
    acceptLabel?: string;
    /** Allow selecting / dropping multiple files */
    multiple?: boolean;
    /** Render upload UI inline instead of in a Dialog */
    embedded?: boolean;
    /** Hide built-in Cancel / Upload buttons (use external controls via actionRef) */
    hideActions?: boolean;
    /** Expose cancel / upload handlers for external buttons */
    actionRef?: React.MutableRefObject<FileUploadDialogActionRef | null>;
    /** Fired when canUpload or uploading changes */
    onActionStateChange?: (state: FileUploadActionState) => void;
    /** Embedded mode: stretch drop zone to fill parent height */
    fillHeight?: boolean;
}

export default function FileUploadDialog(props: FileUploadDialogProps) {
    const {
        open, onClose, onSuccess, onUploadingChange,
        onUpload, title = 'Import Excel',
        accept = '.xlsm', acceptLabel = 'Accepted: .xlsm only · Max size: 100MB',
        multiple = false,
        embedded = false,
        hideActions = false,
        actionRef,
        onActionStateChange,
        fillHeight = false,
    } = props;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const acceptExts = accept.split(',').map(e => e.trim().toLowerCase());

    const isAccepted = (f: File) => acceptExts.some(ext => f.name.toLowerCase().endsWith(ext));

    const reset = useCallback(() => {
        setFiles([]);
        setError(null);
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const handleClose = useCallback(() => {
        if (uploading) return;
        reset();
        onClose();
    }, [uploading, reset, onClose]);

    const takeFiles = (list: FileList | File[]) => {
        const arr = Array.from(list).filter(isAccepted);
        if (arr.length === 0) {
            setError(`Only ${accept} files are accepted`);
            return;
        }
        if (!multiple && arr.length > 1) {
            setFiles([arr[0]]);
        } else {
            setFiles(multiple ? arr : [arr[0]]);
        }
        setError(null);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files?.length) takeFiles(e.dataTransfer.files);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) takeFiles(e.target.files);
    };

    const handleUpload = useCallback(async () => {
        if (files.length === 0) return;
        setUploading(true);
        setError(null);
        onUploadingChange?.(true);
        try {
            const res = multiple
                ? await (onUpload as (files: File[]) => Promise<any>)(files)
                : await (onUpload as (file: File) => Promise<any>)(files[0]);
            reset();
            onSuccess(res);
        } catch (e: any) {
            if (!e?.suppressUploadDialogError) {
                setError(e?.message || 'Import failed');
            }
        } finally {
            setUploading(false);
            onUploadingChange?.(false);
        }
    }, [files, multiple, onUpload, onUploadingChange, reset, onSuccess]);

    const actionState: FileUploadActionState = {
        canUpload: files.length > 0 && !uploading,
        uploading,
    };

    useEffect(() => {
        onActionStateChange?.(actionState);
    }, [actionState.canUpload, actionState.uploading, onActionStateChange]);

    useEffect(() => {
        if (!actionRef) return;
        actionRef.current = {
            cancel: handleClose,
            upload: handleUpload,
            getState: () => actionState,
        };
        return () => {
            actionRef.current = null;
        };
    }, [actionRef, handleClose, handleUpload, actionState.canUpload, actionState.uploading]);

    const fileLabel = files.length === 0
        ? `Drag & drop ${accept} file${multiple ? '(s)' : ''} here or click to browse`
        : files.length === 1
            ? files[0].name
            : `${files.length} files selected`;

    const dropZone = (
        <Box
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
                border: '2px dashed',
                borderColor: files.length ? 'success.main' : 'divider',
                borderRadius: 2,
                p: embedded ? 3 : 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: files.length ? 'success.50' : 'action.hover',
                transition: 'all 0.2s',
                '&:hover': {borderColor: 'primary.main', bgcolor: 'action.selected'},
                ...(fillHeight ? {
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                } : {}),
            }}
        >
            <CloudUploadIcon sx={{fontSize: embedded ? 40 : 48, color: 'text.secondary', mb: 1}}/>
            <Typography variant="body1">{fileLabel}</Typography>
            {files.length > 1 && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.5, px: 1}}>
                    {files.map(f => f.name).join(' · ')}
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
                {acceptLabel}
            </Typography>
        </Box>
    );

    const uploadBody = fillHeight ? (
        <Box sx={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0}}>
            {dropZone}
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                hidden
                onChange={handleFileSelect}
            />
            {uploading && <LinearProgress sx={{mt: 2, flexShrink: 0}}/>}
            {error && <Alert severity="error" sx={{mt: 2, flexShrink: 0}}>{error}</Alert>}
        </Box>
    ) : (
        <>
            {dropZone}
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                hidden
                onChange={handleFileSelect}
            />
            {uploading && <LinearProgress sx={{mt: 2}}/>}
            {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
        </>
    );

    const actionButtons = (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: embedded ? 2 : 0}}>
            <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
            <Button
                variant="contained"
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
            >
                Upload
            </Button>
        </Box>
    );

    if (embedded) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: fillHeight ? '100%' : undefined,
                minHeight: fillHeight ? 0 : undefined,
                flex: fillHeight ? 1 : undefined,
            }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1.5} sx={{flexShrink: 0}}>
                    {title}
                </Typography>
                {uploadBody}
                {!hideActions && actionButtons}
            </Box>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {uploadBody}
            </DialogContent>
            {!hideActions && (
                <DialogActions>
                    <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={files.length === 0 || uploading}
                    >
                        Upload
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}
